import type { ReactNode } from 'react';
import type { Cadence, ReportCustomerDto } from '@act/shared/data-transfer-objects';
import { reportSpriteHtml } from './sections/reportBodyHtml.util';
import {
  type ReportReadingColumnItem,
  toPreviewCustomerInput,
} from './reportPreview.adapter.util';
import { ReportPreviewShell } from './ReportPreviewShell';
import {
  ReportSectionItemView,
  type ReportSectionItemRenderMode,
} from './ReportSectionItemView';

/** Per-section floor for print-fit scaling (see reportPrintFit.util). */
const REPORT_PRINT_MIN_SCALE_BY_SECTION: Partial<Record<string, string>> = {
  'engagement-story': '0.82',
  'vbp-engagement': '0.72',
  'who-were-reaching': '0.58',
  'what-members-need': '0.5',
  'quality-measure-projections': '0.6',
  'connections-behavioral-care': '0.48',
};

export interface ReportReadingColumnProps {
  customer: ReportCustomerDto;
  reportingPeriod: string;
  measurementPeriod?: string;
  cadence: Cadence;
  items: ReportReadingColumnItem[];
  mode: ReportSectionItemRenderMode;
  header?: ReactNode;
  reportId?: string;
}

/**
 * Full-bleed editorial report preview (Studio PreviewReadingColumn parity).
 */
export const ReportReadingColumn = ({
  customer,
  reportingPeriod,
  measurementPeriod,
  cadence,
  items,
  mode,
  header,
  reportId,
}: ReportReadingColumnProps) => {
  const sorted = [...items].sort((a, b) => a.order - b.order);
  const customerInput = toPreviewCustomerInput(customer);

  return (
    <ReportPreviewShell>
      <main className="reema-report report-theme">
        <div
          className="report-sprite-sheet"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: reportSpriteHtml }}
        />

        {header ? (
          <div className="mx-auto max-w-4xl px-6 pt-4 text-center text-xs text-[color:var(--midnight-75)]">
            {header}
          </div>
        ) : null}

        {sorted.length === 0 ? (
          <section className="section section--tan">
            <div className="mx-auto max-w-3xl text-center text-sm text-[color:var(--midnight-75)]">
              No sections included yet — toggle at least one on.
            </div>
          </section>
        ) : (
          sorted.map(item => (
            <div
              key={`${item.sectionId}-${item.order}`}
              className="report-print-section"
              data-report-section-id={item.sectionId}
              {...(REPORT_PRINT_MIN_SCALE_BY_SECTION[item.sectionId]
                ? {
                    'data-report-print-min-scale':
                      REPORT_PRINT_MIN_SCALE_BY_SECTION[item.sectionId],
                  }
                : {})}
            >
              {item.sectionId !== 'hero' ? (
                <div
                  className="report-print-section__safe-top"
                  aria-hidden
                />
              ) : null}
              <div className="report-print-section__fit">
                <ReportSectionItemView
                  item={item}
                  customer={customerInput}
                  reportingPeriod={reportingPeriod}
                  measurementPeriod={measurementPeriod}
                  mode={mode}
                  reportId={reportId}
                  cadence={cadence}
                />
              </div>
            </div>
          ))
        )}
      </main>
    </ReportPreviewShell>
  );
};

export default ReportReadingColumn;
